import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Badge } from 'react-bootstrap';
import { FaEnvelope, FaPhone, FaUsers, FaNewspaper } from 'react-icons/fa';
import apiClient from '../../apiClient';

const COMMUNICATION_TYPES = [
    { 
        value: 'email', 
        label: 'Email', 
        icon: <FaEnvelope />,
        description: 'Send email notifications to all stakeholders'
    },
    { 
        value: 'phone', 
        label: 'Phone', 
        icon: <FaPhone />,
        description: 'Record phone call communications'
    },
    { 
        value: 'meeting', 
        label: 'Meeting', 
        icon: <FaUsers />,
        description: 'Schedule or record meeting communications'
    },
    { 
        value: 'newsletter', 
        label: 'Newsletter', 
        icon: <FaNewspaper />,
        description: 'Include in newsletter distribution'
    }
];

const SendCommunicationModal = ({ show, onHide, event, onSuccess }) => {
    const [communicationType, setCommunicationType] = useState('email');
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSend = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await apiClient.post(`/events/${event.id}/send-communications`, {
                communication_type: communicationType,
                additional_notes: additionalNotes
            });
            
            onSuccess(response.data);
            onHide();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send communications');
        } finally {
            setLoading(false);
        }
    };

    if (!event) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Send Event Communications</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                
                <div className="mb-4">
                    <h5>Event Details</h5>
                    <div className="p-3 bg-light rounded">
                        <strong>{event.name}</strong>
                        <div className="text-muted">{event.event_type}</div>
                        <div className="mt-2">
                            <Badge bg="secondary" className="me-2">
                                {new Date(event.event_date).toLocaleDateString()}
                            </Badge>
                            <Badge bg={event.importance_level === 'High' ? 'danger' : 
                                     event.importance_level === 'Medium' ? 'warning' : 'info'}>
                                {event.importance_level} Priority
                            </Badge>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <h5>Recipients</h5>
                    <p className="text-muted">
                        Communications will be sent to all stakeholders in the selected groups:
                    </p>
                    <div>
                        {event.stakeholder_groups?.map(group => (
                            <Badge key={group.id} bg="primary" className="me-2 mb-1">
                                {group.name}
                            </Badge>
                        )) || <span className="text-muted">No stakeholder groups selected</span>}
                    </div>
                </div>

                <Form.Group className="mb-4">
                    <Form.Label>Communication Type</Form.Label>
                    {COMMUNICATION_TYPES.map(type => (
                        <Form.Check
                            key={type.value}
                            type="radio"
                            id={`type-${type.value}`}
                            label={
                                <div>
                                    <span className="me-2">{type.icon}</span>
                                    <strong>{type.label}</strong>
                                    <div className="text-muted small">{type.description}</div>
                                </div>
                            }
                            value={type.value}
                            checked={communicationType === type.value}
                            onChange={(e) => setCommunicationType(e.target.value)}
                            className="mb-2"
                        />
                    ))}
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Additional Notes (Optional)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={additionalNotes}
                        onChange={(e) => setAdditionalNotes(e.target.value)}
                        placeholder="Add any additional context or customization for this communication..."
                    />
                </Form.Group>

                <div className="alert alert-info">
                    <strong>What will happen:</strong>
                    <ul className="mb-0 mt-2">
                        <li>Individual communication records will be created for each stakeholder</li>
                        <li>Communications will include event details, themes, and key messages</li>
                        <li>All communications will be tracked in the Communication Dashboard</li>
                        <li>Status can be updated as communications are delivered</li>
                    </ul>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSend} 
                    disabled={loading || !event.stakeholder_groups?.length}
                >
                    {loading ? 'Sending...' : 'Send Communications'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SendCommunicationModal;